import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Navigate, Route, Routes, useParams } from "react-router-dom";
import { AuthProvider } from "./auth/AuthContext";
import { RequireAuth } from "./auth/RequireAuth";
import { AppShell } from "./components/layout/AppShell";
import { LoginPage } from "./pages/LoginPage";
import { OverviewPage } from "./pages/OverviewPage";
import { RegulationChatPage } from "./pages/RegulationChatPage";
import { PortfolioDetailPage } from "./pages/PortfolioDetailPage";
import { PipelinePage } from "./pages/PipelinePage";
import { ExposurePage } from "./pages/ExposurePage";
import { SourcesPage } from "./pages/SourcesPage";
import { DigestPage } from "./pages/DigestPage";
import { SettingsPage } from "./pages/SettingsPage";
import { GenieFeedbackPage } from "./pages/GenieFeedbackPage";
import { GenieQuestionLogPage } from "./pages/GenieQuestionLogPage";
import { DocumentFactsPage } from "./pages/DocumentFactsPage";
import { ValueRecoveredReportPage } from "./pages/reports/ValueRecoveredReportPage";
import { CilChangeEventDetailPage, CilChangeEventsReportPage, CilFileEventsPage } from "./pages/reports/CilChangeEventsReportPage";
import { CilCoveragePage } from "./pages/reports/CilCoveragePage";
import { TrueupGapsPage } from "./pages/reports/TrueupGapsPage";
import { PotentialClaimsReportPage } from "./pages/reports/PotentialClaimsReportPage";
import { InProgressClaimsReportPage } from "./pages/reports/InProgressClaimsReportPage";
import { RejectedClaimsReportPage } from "./pages/reports/RejectedClaimsReportPage";
import { ReportClaimDetailPage } from "./pages/reports/ReportClaimDetailPage";
import { PopupShell } from "./components/layout/PopupShell";
import { DisputeThreadPage } from "./pages/DisputeThreadPage";
import { MatterTrackerPage } from "./pages/MatterTrackerPage";
import { SearchPage } from "./pages/SearchPage";
import { TabularReviewPage } from "./pages/TabularReviewPage";
import { ComingSoonPage } from "./pages/ComingSoonPage";
import { Clock, Flag, Gavel, AlertTriangle, Search as SearchIcon, Radio, TrendingUp } from "lucide-react";
import { OVERVIEW_ONLY } from "./lib/demoMode";
import { useRegulationHome } from "./lib/useRegulationHome";
function RedirectLegacyCandidateDetail() {
  const { id = "" } = useParams<{ id: string }>();
  return <Navigate to={`/reports/potential/${id}`} replace />;
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      refetchOnWindowFocus: false,
    },
  },
});

/** The home page: the HP Genie chat or Overview, by the org-wide setting (Settings > Dashboard home). */
function HomeRoute() {
  const { regulationHome, ready } = useRegulationHome();
  if (!ready) return null;
  return regulationHome ? <Navigate to="/regulation" replace /> : <OverviewPage />;
}

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            {/* Claims popup (opened from the Overview cards): same pages, no app chrome. */}
            <Route
              path="/popup"
              element={
                <RequireAuth>
                  <PopupShell />
                </RequireAuth>
              }
            >
              <Route path="recovered" element={<ValueRecoveredReportPage basePath="/popup/recovered" />} />
              <Route path="recovered/:id" element={<ReportClaimDetailPage />} />
              <Route path="potential" element={<PotentialClaimsReportPage basePath="/popup/potential" ledgerReadyOnly />} />
              <Route path="potential/:id" element={<ReportClaimDetailPage />} />
              <Route path="rejected" element={<RejectedClaimsReportPage />} />
              <Route path="rejected/:id" element={<ReportClaimDetailPage />} />
              <Route path="findings/:findingId/dispute-thread" element={<DisputeThreadPage />} />
              <Route path="cil-coverage" element={<CilCoveragePage />} />
              <Route path="trueup-gaps" element={<TrueupGapsPage />} />
              <Route path="cil-disallowed-to-allowed" element={<CilChangeEventsReportPage direction="disallowed_to_allowed" />} />
              <Route path="cil-allowed-to-disallowed" element={<CilChangeEventsReportPage direction="allowed_to_disallowed" />} />
              <Route path="cil-file/:jobId/:which" element={<CilFileEventsPage />} />
              <Route path="cil-disallowed-to-allowed/:eventId" element={<CilChangeEventDetailPage direction="disallowed_to_allowed" />} />
              <Route path="cil-allowed-to-disallowed/:eventId" element={<CilChangeEventDetailPage direction="allowed_to_disallowed" />} />
            </Route>
            <Route
              element={
                <RequireAuth>
                  <AppShell />
                </RequireAuth>
              }
            >
              <Route index element={<HomeRoute />} />
              <Route path="regulation" element={<RegulationChatPage />} />
              <Route path="feedback" element={<GenieFeedbackPage />} />
              <Route path="questions" element={<GenieQuestionLogPage />} />
              <Route path="facts" element={<DocumentFactsPage />} />
              <Route path="overview" element={<OverviewPage />} />
              <Route path="portfolio/:agreementId" element={<PortfolioDetailPage />} />
              <Route path="pipeline" element={<PipelinePage />} />
              <Route
                path="exposure"
                element={OVERVIEW_ONLY ? <ComingSoonPage label="Rejected Claims" icon={AlertTriangle} /> : <ExposurePage />}
              />
              <Route
                path="sources"
                element={OVERVIEW_ONLY ? <ComingSoonPage label="Sources" icon={Radio} /> : <SourcesPage />}
              />
              <Route path="digest" element={<DigestPage />} />
              <Route path="settings" element={<SettingsPage />} />

              <Route
                path="reports/recovered"
                element={OVERVIEW_ONLY ? <ComingSoonPage label="Value Recovered" icon={TrendingUp} /> : <ValueRecoveredReportPage />}
              />
              <Route path="reports/recovered/:id" element={<ReportClaimDetailPage />} />
              <Route
                path="reports/potential"
                element={OVERVIEW_ONLY ? <ComingSoonPage label="Potential Claims" icon={Flag} /> : <PotentialClaimsReportPage />}
              />
              <Route path="reports/potential/:id" element={<ReportClaimDetailPage />} />
              <Route
                path="reports/in-progress"
                element={OVERVIEW_ONLY ? <ComingSoonPage label="In Progress" icon={Clock} /> : <InProgressClaimsReportPage />}
              />
              <Route path="reports/in-progress/:id" element={<ReportClaimDetailPage />} />
              <Route path="reports/gaps" element={<Navigate to="/" replace />} />

              <Route path="candidates" element={<Navigate to="/reports/potential" replace />} />
              <Route path="candidates/:id" element={<RedirectLegacyCandidateDetail />} />
              <Route path="findings/:findingId/dispute-thread" element={<DisputeThreadPage />} />
              <Route
                path="matters"
                element={OVERVIEW_ONLY ? <ComingSoonPage label="Matter Tracker" icon={Gavel} /> : <MatterTrackerPage />}
              />
              <Route
                path="search"
                element={OVERVIEW_ONLY ? <ComingSoonPage label="Search" icon={SearchIcon} /> : <SearchPage />}
              />
              <Route path="tabular-review" element={<TabularReviewPage />} />
            </Route>
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </QueryClientProvider>
  );
}
